import { useEffect } from "react"
import { Modal, Form, Input, Select, DatePicker } from "antd"
import { StudentModalProps, StudentFormData, StudentStatus } from "../types"
import {
  StudentStatusLabels,
  NAME_VALIDATION,
  NOTE_VALIDATION,
} from "../constants"
import dayjs, { Dayjs } from "dayjs"

const { Option } = Select
const { TextArea } = Input

// 内部表单数据类型，用于处理Dayjs对象
interface InternalFormData {
  name: string
  status: StudentStatus
  note?: string
  birthday?: Dayjs
}

const StudentModal = ({
  isOpen,
  onClose,
  onSave,
  student,
  loading = false,
}: StudentModalProps) => {
  const [form] = Form.useForm<InternalFormData>()

  const convertToStudentFormat = (data: InternalFormData): StudentFormData => ({
    name: data.name,
    status: data.status,
    note: data.note,
    birthday: data.birthday ? data.birthday.format("YYYY-MM-DD") : undefined,
  })

  // 编辑模式：填充现有数据
  useEffect(() => {
    if (isOpen && student) {
      form.setFieldsValue({
        name: student.name,
        status: student.status,
        note: student.note,
        birthday: student.birthday ? dayjs(student.birthday) : undefined,
      })
      return
    }

    if (isOpen) {
      form.setFieldsValue({
        status: StudentStatus.INTERVENING,
      })
    }
  }, [form, isOpen, student])

  // 处理表单提交
  const handleSubmit = async () => {
    const values = await form.validateFields()
    const formData = convertToStudentFormat(values)
    onSave(formData)
  }

  // 处理取消
  const handleCancel = () => {
    onClose()
  }

  return (
    <Modal
      title={student ? "编辑学生信息" : "新增学生"}
      open={isOpen}
      onOk={handleSubmit}
      onCancel={handleCancel}
      confirmLoading={loading}
      width={600}
      destroyOnHidden
      okText="确定"
      cancelText="取消"
    >
      <Form form={form} layout="vertical" preserve={false}>
        <Form.Item
          name="name"
          label="学生姓名"
          rules={[{ required: true, message: NAME_VALIDATION.REQUIRED_MESSAGE }]}
        >
          <Input
            placeholder="请输入学生姓名"
            maxLength={NAME_VALIDATION.MAX_LENGTH}
          />
        </Form.Item>
        <Form.Item
          name="status"
          label="状态"
          rules={[{ required: true, message: "请选择学生状态" }]}
        >
          <Select placeholder="请选择学生状态">
            {Object.values(StudentStatus)
              .filter((value) => typeof value === "number")
              .map((status) => (
                <Option key={status} value={status}>
                  {StudentStatusLabels[status as StudentStatus]}
                </Option>
              ))}
          </Select>
        </Form.Item>
        <Form.Item name="birthday" label="生日">
          <DatePicker
            placeholder="请选择生日"
            style={{ width: "100%" }}
            format="YYYY-MM-DD"
          />
        </Form.Item>
        <Form.Item name="note" label="备注">
          <TextArea
            placeholder="请输入备注信息（可选）"
            rows={4}
            maxLength={NOTE_VALIDATION.MAX_LENGTH}
            showCount
          />
        </Form.Item>
      </Form>
    </Modal>
  )
}

export default StudentModal
